import React, { useCallback, useMemo } from 'react';
import type { SelectProps } from 'antd';
import { Select, Spin, Tooltip } from 'antd';
import IntlMessages from '@src/util/IntlMessages';

// ─── Constants ────────────────────────────────────────────────────────────────

export const ALL_OPTION_VALUE = 'all';

const INTERNAL_ALL_KEY = '__all__';
const DEFAULT_MAX_NAME_LENGTH = 20;
const DEFAULT_DROPDOWN_WIDTH = 300;
const DEFAULT_LIST_HEIGHT = 200;
const DEFAULT_MAX_TAG_COUNT = 2;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BaseSelectorProps<T = Record<string, unknown>> {
  items: T[];
  loading?: boolean;
  loadingMore?: boolean;
  handlePopupScroll?: React.UIEventHandler<HTMLElement>;
  getPopupContainer?: SelectProps['getPopupContainer'];
  onSearch?: (value: string) => void;
  onReset?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: any) => void;
  allLabelId?: string;
  allValue?: string | number | string[];
  placeholderId?: string;
  mode?: 'multiple' | 'tags';
  showAllOption?: boolean;
  /** Disable for paginated lists: `items` holds only the loaded page, never the whole set. */
  autoSelectAllOnComplete?: boolean;
  maxNameLength?: number;
  getItemName?: (item: T) => string;
  getItemId?: (item: T) => string;
  renderOption?: (item: T) => React.ReactNode;
  dropdownWidth?: number;
  listHeight?: number;
  maxTagCount?: SelectProps['maxTagCount'];
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  id?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const BaseSelector = <T = Record<string, unknown>,>({
  items,
  loading,
  loadingMore = false,
  handlePopupScroll,
  onSearch,
  onReset,
  value,
  onChange,
  allLabelId,
  allValue = ALL_OPTION_VALUE,
  placeholderId,
  mode,
  showAllOption = true,
  autoSelectAllOnComplete = true,
  maxNameLength = DEFAULT_MAX_NAME_LENGTH,
  getItemName = (item: T) => (item as Record<string, unknown>).name as string,
  getItemId = (item: T) => (item as Record<string, unknown>).id as string,
  renderOption,
  dropdownWidth = DEFAULT_DROPDOWN_WIDTH,
  listHeight = DEFAULT_LIST_HEIGHT,
  maxTagCount = DEFAULT_MAX_TAG_COUNT,
  disabled,
  ...restProps
}: BaseSelectorProps<T>): JSX.Element => {
  const isMultiple = mode === 'multiple' || mode === 'tags';
  const shouldShowAllOption = isMultiple && showAllOption && allLabelId;
  const isAllValueArray = Array.isArray(allValue);
  const internalAllValue = isAllValueArray ? INTERNAL_ALL_KEY : (allValue as string | number);

  const renderOptionContent = useCallback(
    (item: T) => {
      if (renderOption) {
        return renderOption(item);
      }
      const name = getItemName(item);
      if (name?.length > maxNameLength) {
        return <Tooltip title={name}>{name.substring(0, maxNameLength)} ...</Tooltip>;
      }
      return name;
    },
    [renderOption, getItemName, maxNameLength],
  );

  const optionNodes = useMemo(() => {
    const itemOptions = items.map(item => {
      const id = getItemId(item);
      return (
        <Select.Option key={id} value={id}>
          {renderOptionContent(item)}
        </Select.Option>
      );
    });

    if (loadingMore) {
      itemOptions.push(
        <Select.Option key="__loading__" disabled>
          <div style={{ textAlign: 'center', padding: '8px' }}>
            <Spin size="small" />
          </div>
        </Select.Option>,
      );
    }

    return itemOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items?.length, items?.map(getItemId).join(','), loadingMore, renderOptionContent, getItemId]);

  const allTargetIds = useMemo(
    () =>
      isAllValueArray
        ? (allValue as string[])
        : items.map(item => getItemId(item)),
    [allValue, getItemId, isAllValueArray, items],
  );

  const isAllSelected = useCallback(
    (val: unknown) => {
      if (isAllValueArray) {
        const valueArray = Array.isArray(val) ? (val as string[]) : [];
        return (
          allTargetIds.length > 0 &&
          allTargetIds.length === valueArray.length &&
          allTargetIds.every(id => valueArray.includes(id))
        );
      }
      return val === allValue;
    },
    [allTargetIds, allValue, isAllValueArray],
  );

  const isCurrentlyAllSelected = useMemo(() => {
    if (!isMultiple || !shouldShowAllOption) return false;
    // null/undefined means no selection — do not treat as "all"
    if (value == null) return false;
    const valueArray = Array.isArray(value) ? (value as string[]) : [];
    if (valueArray.length === 0) return false;
    const lastSelected = valueArray[valueArray.length - 1];
    return lastSelected === internalAllValue || isAllSelected(value);
  }, [value, isMultiple, shouldShowAllOption, internalAllValue, isAllSelected]);

  // When "all" is selected, include all item IDs so the dropdown shows them as checked.
  // tagRender hides those individual tags so the label only shows "Todos".
  const selectedValue = useMemo(() => {
    if (!isMultiple) return value;
    if (!shouldShowAllOption) return value;

    if (isCurrentlyAllSelected) {
      return [internalAllValue, ...allTargetIds];
    }

    const valueArray = Array.isArray(value) ? (value as string[]) : [];
    return valueArray.filter(v => v !== internalAllValue);
  }, [
    value,
    isMultiple,
    shouldShowAllOption,
    isCurrentlyAllSelected,
    internalAllValue,
    allTargetIds,
  ]);

  // Hide individual item tags when "all" is active so only "Todos" appears in the label.
  const tagRender = useMemo<SelectProps['tagRender']>(() => {
    if (!isCurrentlyAllSelected || !isMultiple) return undefined;
    return ({ label, value: tagValue, onClose, closable }) => {
      if (tagValue === internalAllValue) {
        return (
          <span className="ant-select-selection-item">
            <span className="ant-select-selection-item-content">{label}</span>
            {closable && (
              <span
                className="ant-select-selection-item-remove"
                onMouseDown={e => e.preventDefault()}
                onClick={onClose}
              >
                <span className="material-symbols-outlined gx-fs-md">close</span>
              </span>
            )}
          </span>
        );
      }
      return <span style={{ display: 'none' }} />;
    };
  }, [isCurrentlyAllSelected, isMultiple, internalAllValue]);

  const handleChange = useCallback(
    (newValue: string | string[]) => {
      if (!onChange) return;

      if (!isMultiple) {
        onChange(newValue);
        return;
      }

      if (!shouldShowAllOption) {
        onChange(newValue);
        return;
      }

      const arr = newValue as string[];
      const lastSelected = arr[arr.length - 1];

      if (arr.length === 0) {
        onChange([]);
        return;
      }

      if (lastSelected === internalAllValue) {
        onChange(isAllValueArray ? allValue : [allValue]);
        return;
      }

      const filtered = arr.filter(v => v !== internalAllValue);

      if (
        isCurrentlyAllSelected &&
        allTargetIds.length === filtered.length &&
        allTargetIds.every(id => filtered.includes(id))
      ) {
        onChange([]);
        return;
      }

      // Auto-select "all" when every allowed/target item is selected
      if (
        autoSelectAllOnComplete &&
        allTargetIds.length > 0 &&
        allTargetIds.every(id => filtered.includes(id))
      ) {
        onChange(isAllValueArray ? allValue : [allValue]);
        return;
      }

      onChange(filtered);
    },
    [
      onChange,
      onReset,
      isMultiple,
      shouldShowAllOption,
      internalAllValue,
      allValue,
      isAllValueArray,
      allTargetIds,
      isCurrentlyAllSelected,
      autoSelectAllOnComplete,
    ],
  );

  const handleClear = useCallback(() => {
    onReset?.();
    if (!onChange) return;
    onChange(isMultiple ? [] : undefined);
  }, [onReset, onChange, isMultiple]);

  const selectProps: SelectProps = {
    loading,
    disabled,
    placeholder: placeholderId ? <IntlMessages id={placeholderId} /> : undefined,
    onChange: handleChange as SelectProps['onChange'],
    value: selectedValue,
    allowClear: true,
    className: 'gx-m-0',
    dropdownMatchSelectWidth: false,
    dropdownStyle: { width: dropdownWidth },
    onSearch,
    onClear: handleClear,
    getPopupContainer: trigger => trigger.parentNode as HTMLElement,
    onPopupScroll: handlePopupScroll,
    listHeight,
    showSearch: true,
    filterOption: false,
    showArrow: true,
    ...(tagRender ? { tagRender } : {}),
    ...(restProps as SelectProps),
  };

  if (isMultiple) {
    selectProps.mode = mode;
    // When "all" is selected, prevent the "+N more" overflow by allowing enough slots
    // for all hidden tags; only the "Todos" tag is visible via tagRender.
    selectProps.maxTagCount = isCurrentlyAllSelected ? allTargetIds.length + 1 : maxTagCount;
  }

  return (
    <Select {...selectProps}>
      {shouldShowAllOption && (
        <Select.Option key={String(internalAllValue)} value={internalAllValue}>
          <IntlMessages id={allLabelId ?? 'general.all'} />
        </Select.Option>
      )}
      {optionNodes}
    </Select>
  );
};

export default BaseSelector;
